import { useEffect } from 'react';
import { ApplicationModel, ApplicationStep, DefaultApplicationModel } from '../types';
import { updateApplication, getAffiliateHeardAboutUs } from '../services';
import { ApplicationActions } from '../state/actions';

interface UseApplicationEffectsProps {
  createdUser: boolean | null | undefined;
  storageToken: string | null | undefined;
  isSaving: boolean | undefined;
  application: ApplicationModel | null | undefined;
  submittedFormData: ApplicationModel | null;
  affiliate: string | undefined;
  dispatch: any;
  storage: any;
  localStorage: any;
  setSubmittedFormData: (data: ApplicationModel | null) => void;
  setShowSuccessMessage?: (show: boolean) => void;
  setIsShowingSuccess?: (show: boolean) => void;
}

export const useApplicationEffects = ({
  createdUser,
  storageToken,
  isSaving,
  application,
  submittedFormData,
  affiliate,
  dispatch,
  storage,
  localStorage,
  setSubmittedFormData,
  setShowSuccessMessage,
  setIsShowingSuccess,
}: UseApplicationEffectsProps) => {
  // Handle CreateUser response
  useEffect(() => {
    const storeTokenInLocalStorage = async (token: string) => {
      const { SaveTokenInStorage } = await import('@tradingblock/storage');
      await SaveTokenInStorage(localStorage, token);
    };

    // when a new user is created and authenticated, save application to continue to next step
    if (createdUser === true && storageToken && !isSaving) {
      // Show success message first
      if (setShowSuccessMessage) {
        setShowSuccessMessage(true);
      }
      if (setIsShowingSuccess) {
        setIsShowingSuccess(true);
      }

      // Add a delay to allow the success message to be visible before saving application
      setTimeout(() => {
        // Clear success message states
        if (setShowSuccessMessage) {
          setShowSuccessMessage(false);
        }
        if (setIsShowingSuccess) {
          setIsShowingSuccess(false);
        }

        // store token in localstorage first
        storeTokenInLocalStorage(storageToken);

        // Use the submitted form data if available, otherwise fall back to application state
        let applicationDataToSave = submittedFormData || application || DefaultApplicationModel;

        // Handle affiliate data if present
        if (affiliate) {
          const affiliateValue = getAffiliateHeardAboutUs(affiliate);
          if (affiliateValue && !applicationDataToSave.hearAboutUsId && !applicationDataToSave.repId) {
            let repId = affiliateValue.repId;

            // ESTI/ESTP Specific Rep IDs due to Multiple Representatives
            if (affiliate === 'ESTI') {
              repId = affiliateValue.representatives[0].repId;
            } else if (affiliate === 'ESTP') {
              repId = affiliateValue.representatives[1].repId;
            }

            applicationDataToSave = {
              ...applicationDataToSave,
              hearAboutUsId: affiliateValue.heardAboutId,
              repId: repId,
            };
          }
        }

        // Since user credentials are now collected on the home page,
        // mark SecureAccount as completed but still allow user to complete UserInfoSection
        const saveStep = ApplicationStep.SecureAccount;
        const data = updateApplication(saveStep, false, true, applicationDataToSave, 1);
        dispatch(
          ApplicationActions.requestSaveApplication({
            application: data,
            saveType: 'continue',
            saveStep,
            storage,
          })
        );

        // Clear the submitted form data after using it
        setSubmittedFormData(null);
      }, 2000); // 2 second delay to show success message
    }
  }, [
    storageToken,
    createdUser,
    isSaving,
    application,
    submittedFormData,
    dispatch,
    storage,
    localStorage,
    affiliate,
    setSubmittedFormData,
    setShowSuccessMessage,
    setIsShowingSuccess,
  ]);
};
